// ════════════════════════════════════════════════════════════
//  unstableWorld.jsx — THE BACKDROP OF THE UNSTABLE WORLD
// ════════════════════════════════════════════════════════════
//  caP'n's ruling, and it is a STANDING one rather than a request
//  for this track:
//
//    "remember this is the unstable world — always shattered
//     pocket dimensions, black holes that are actually red or
//     purple, cracking eclipses, oceans of Khao or Anima,
//     radiating Lyt and Lyf… if I don't mention specific
//     landscapes this is the fallback."
//
//  So this file is not scenery for Ruin Rush. It is Ruin Rush's
//  implementation of a shared vocabulary, and it is written to be
//  lifted into any IE title that needs a horizon.
//
//  ── WHY A BACKDROP AT ALL ───────────────────────────────────
//  The road pass made the circuit read. It also made it obvious
//  that the circuit was a lit ribbon floating in a black void:
//  everything past the verge was nothing. A racing game is looked
//  AT more than it is looked down on, and the horizon is most of
//  what the player actually sees.
//
//  ── THE SIX ELEMENTS (the canon vocabulary) ─────────────────
//   1 THE CRACKED ECLIPSE — a dark sun with a burning rim, split
//     by a fracture. The Unstable World "cracked open"; this is
//     that sentence as an object, and it is the signature.
//   2 THE RUIN HOLE — a black hole that is emphatically NOT black:
//     a red or violet accretion disc around a lensing ring.
//   3 SHATTERED POCKET DIMENSIONS — angular islands adrift, each
//     lit from INSIDE, because a pocket dimension has something
//     in it. They turn; they do not orbit anything.
//   4 THE OCEAN — Khao or Anima, a vast luminous liquid far below
//     the road. The track hangs over it.
//   5 RADIATING LYT / LYF — columns rising off the ocean. Lyt is
//     the light that harms; Lyf is the skin that endures.
//   6 THE RISE — motes going UP. Everything in the Empire rises;
//     that is why the Slag Golem dripping was legible. Direction
//     is characterisation, so the ambient drift is chosen, not
//     defaulted.
//
//  ── THE RULES THIS FILE OBEYS ───────────────────────────────
//  · ZERO NEW LIGHTS. Every glow is emissive or meshBasic. Seven
//    karts already multiply every material's cost scene-wide.
//  · `fog={false}` on everything out here. The fog far plane sits
//    at ~2.6 × the track radius, so an un-flagged backdrop is
//    just fog-coloured mush. Same trick the sky dome uses.
//  · SEEDED, NEVER RANDOM, and never during render. Every
//    position is baked in a useMemo off mulberry32.
//  · ONE useFrame PER SYSTEM, not one per object. Twenty-six
//    shards on twenty-six callbacks is twenty-six callbacks a
//    frame for a result that reads identically.
//  · POPULATION COUNTS ARE LABELLED DIALS (below). If this ever
//    costs frames, these are the first and only things to touch —
//    never the geometry, and never the look.
// ════════════════════════════════════════════════════════════
import { useRef, useMemo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { rnd } from "./ieBeingModels";

export const UNSTABLE_WORLD_VERSION = "1.0.0";

// ── THE DIALS ────────────────────────────────────────────────
// Touch these before anything else if the backdrop ever costs a
// frame. Nothing here changes what the world IS, only how much
// of it there is.
const N_SHARDS  = 26;   // shattered pocket dimensions
const N_COLUMNS = 7;    // Lyt / Lyf radiance off the ocean
const N_MOTES   = 22;   // the rise
const N_SHATTER = 5;    // distinct shard geometries, reused across all shards

// ── THE CANON COLOURS ────────────────────────────────────────
// Named so nobody has to remember which hex is which force.
export const IE_LIGHT = {
  nullus: "#B388FF",   // the nothing
  omnis:  "#F2C14E",   // the everything
  anima:  "#FF4D8D",   // life, spendable
  khao:   "#7CE7FF",   // the well
  lyt:    "#FFE9A0",   // the light that harms
  lyf:    "#7FE8D8",   // the skin that endures
  ruin:   "#E8453C",   // what a black hole is actually the colour of
  shade:  "#2A1540",
};

// ════════════════════════════════════════════════════════════
//  1 · THE CRACKED ECLIPSE
// ════════════════════════════════════════════════════════════
//  A dark disc with a burning corona, and a fracture across it
//  that glows hotter than the rim — the world cracked open, and
//  it did not heal. Painted, not modelled: one cached canvas
//  doing the work of a dozen meshes, per the being-kit recipe.
function eclipseTex(key, rim, crack) {
  const c = document.createElement("canvas");
  c.width = 512; c.height = 512;
  const g = c.getContext("2d");
  const R = rnd(hash(key));
  g.clearRect(0, 0, 512, 512);

  // the corona — light escaping past the occluded disc
  const cor = g.createRadialGradient(256, 256, 150, 256, 256, 250);
  cor.addColorStop(0, "rgba(0,0,0,0)");
  cor.addColorStop(0.62, rim + "00");
  cor.addColorStop(0.72, rim + "CC");
  cor.addColorStop(0.78, rim + "66");
  cor.addColorStop(1, rim + "00");
  g.fillStyle = cor;
  g.beginPath(); g.arc(256, 256, 250, 0, 7); g.fill();

  // the disc itself — not black, a deep bruise
  g.fillStyle = "#140A22";
  g.beginPath(); g.arc(256, 256, 168, 0, 7); g.fill();

  // the burning rim
  g.strokeStyle = rim; g.lineWidth = 5;
  g.shadowColor = rim; g.shadowBlur = 26;
  g.beginPath(); g.arc(256, 256, 168, 0, 7); g.stroke();
  g.shadowBlur = 0;

  // ── THE CRACK ──
  // A jagged seam across the disc, branching once. Seeded, so the
  // eclipse is the same eclipse every time the game loads — a
  // landmark you can learn is worth more than novelty.
  const drawCrack = (x0, y0, x1, y1, w, depth) => {
    g.strokeStyle = crack; g.lineWidth = w;
    g.shadowColor = crack; g.shadowBlur = 18;
    g.beginPath(); g.moveTo(x0, y0);
    const segs = 9;
    for (let i = 1; i <= segs; i++) {
      const t = i / segs;
      const jx = (R() - 0.5) * 46 * (1 - Math.abs(t - 0.5) * 1.2);
      const jy = (R() - 0.5) * 46 * (1 - Math.abs(t - 0.5) * 1.2);
      g.lineTo(x0 + (x1 - x0) * t + jx, y0 + (y1 - y0) * t + jy);
    }
    g.stroke(); g.shadowBlur = 0;
    if (depth > 0) {
      const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
      drawCrack(mx, my, mx + (R() - 0.5) * 200, my + (R() - 0.5) * 200, w * 0.55, depth - 1);
    }
  };
  drawCrack(112, 132, 396, 372, 6, 2);

  // heat bleeding out of the fracture
  g.globalAlpha = 0.32; g.fillStyle = crack;
  for (let i = 0; i < 40; i++) {
    const a = R() * 7, d = R() * 160;
    g.beginPath(); g.arc(256 + Math.cos(a) * d, 256 + Math.sin(a) * d, 1 + R() * 3, 0, 7); g.fill();
  }
  g.globalAlpha = 1;

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function CrackedEclipse({ pos, size, rim, crack }) {
  const tex = useMemo(() => eclipseTex("eclipse_" + rim + crack, rim, crack), [rim, crack]);
  const g = useRef();
  const { camera } = useThree();
  useFrame(({ clock }) => {
    // it does not spin. It BREATHES — a crack that pulses reads as
    // a wound, and a wound is the point.
    if (!g.current) return;
    const t = clock.elapsedTime;
    g.current.scale.setScalar(1 + Math.sin(t * 0.28) * 0.012);
    // ⚑ BILLBOARD. These are planes in a world the player orbits
    // freely; without this the signature object of the Unstable
    // World is edge-on and invisible from most of the circuit.
    g.current.lookAt(camera.position);
  });
  return (
    <group ref={g} position={pos}>
      <mesh>
        <planeGeometry args={[size, size]} />
        <meshBasicMaterial map={tex} transparent depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  2 · THE RUIN HOLE — a black hole that is red, or violet
// ════════════════════════════════════════════════════════════
function RuinHole({ pos, size, hot, cool }) {
  const disc = useRef(), ring = useRef();
  const tex = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 512; c.height = 512;
    const g = c.getContext("2d");
    const R = rnd(hash("ruinhole"));
    // the accretion disc — banded, hotter toward the middle
    for (let i = 0; i < 46; i++) {
      const r = 120 + i * 4.2;
      g.strokeStyle = i % 3 === 0 ? hot : cool;
      g.globalAlpha = (1 - i / 46) * 0.5 + R() * 0.14;
      g.lineWidth = 1 + R() * 3.4;
      g.beginPath(); g.arc(256, 256, r, 0, 7); g.stroke();
    }
    g.globalAlpha = 1;
    // the event horizon — the one honestly black thing in the Empire
    g.fillStyle = "#000000";
    g.beginPath(); g.arc(256, 256, 112, 0, 7); g.fill();
    // the lensing ring, right at the lip
    g.strokeStyle = "#FFFFFF"; g.lineWidth = 2.5;
    g.shadowColor = hot; g.shadowBlur = 30;
    g.beginPath(); g.arc(256, 256, 116, 0, 7); g.stroke();
    g.shadowBlur = 0;
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }, [hot, cool]);
  const grp = useRef();
  const { camera } = useThree();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    // billboard the PAIR, then spin the discs inside it — spinning a
    // billboarded object directly fights the lookAt every frame
    if (grp.current) grp.current.lookAt(camera.position);
    // the disc turns; the halo counter-turns. Two rates is what
    // stops a flat plane reading as a sticker.
    if (disc.current) disc.current.rotation.z = t * 0.055;
    if (ring.current) {
      ring.current.rotation.z = -t * 0.09;
      ring.current.material.opacity = 0.20 + Math.sin(t * 0.7) * 0.07;
    }
  });
  return (
    <group ref={grp} position={pos}>
      <mesh ref={disc}>
        <planeGeometry args={[size, size]} />
        <meshBasicMaterial map={tex} transparent depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
      <mesh ref={ring} scale={1.45}>
        <planeGeometry args={[size, size]} />
        <meshBasicMaterial map={tex} transparent opacity={0.22} depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
//  3 · SHATTERED POCKET DIMENSIONS
// ════════════════════════════════════════════════════════════
//  Angular islands adrift in the void, each lit from INSIDE —
//  because a pocket dimension is not a rock, it is a place with
//  something happening in it. Five geometries shared across
//  twenty-six shards; one hook turns all of them.
function ShatteredDimensions({ bounds, glowA, glowB, seed = 11 }) {
  const geos = useMemo(() => {
    const out = [];
    for (let i = 0; i < N_SHATTER; i++) {
      // dodecahedron/octahedron with detail 0 = faceted, sharp,
      // and nothing like a sphere. Shattered, not eroded.
      const g = i % 2 === 0
        ? new THREE.DodecahedronGeometry(1, 0)
        : new THREE.OctahedronGeometry(1, 0);
      // squash each one differently so no two shards are twins
      const R = rnd(seed * 31 + i);
      g.scale(0.6 + R() * 0.9, 0.24 + R() * 0.4, 0.6 + R() * 0.9);
      out.push(g);
    }
    return out;
  }, [seed]);

  const items = useMemo(() => {
    const R = rnd(seed);
    const out = [];
    const inner = bounds.radius * 1.5, outer = bounds.radius * 2.3;
    for (let i = 0; i < N_SHARDS; i++) {
      const a = R() * Math.PI * 2;
      const d = inner + R() * (outer - inner);
      out.push({
        key: i,
        gi: i % N_SHATTER,
        x: bounds.cx + Math.cos(a) * d,
        z: bounds.cz + Math.sin(a) * d,
        y: bounds.minH + (R() - 0.28) * bounds.radius * 0.75,
        s: bounds.radius * (0.05 + R() * 0.16),
        rot: [R() * 3, R() * 6, R() * 3],
        spin: (R() - 0.5) * 0.035,
        bob: 0.4 + R() * 1.4,
        phase: R() * 7,
        glow: R() < 0.5 ? glowA : glowB,
      });
    }
    return out;
  }, [bounds, glowA, glowB, seed]);

  const refs = useRef([]);
  useFrame(({ clock }) => {
    // ONE hook for all twenty-six. Twenty-six callbacks a frame
    // would read identically and cost twenty-six times as much.
    const t = clock.elapsedTime;
    for (let i = 0; i < refs.current.length; i++) {
      const o = refs.current[i], p = items[i];
      if (!o || !p) continue;
      o.rotation.y = p.rot[1] + t * p.spin;
      o.rotation.x = p.rot[0] + Math.sin(t * 0.12 + p.phase) * 0.06;
      o.position.y = p.y + Math.sin(t * 0.2 + p.phase) * p.bob;
    }
  });

  return items.map((p, i) => (
    <group key={p.key} ref={el => (refs.current[i] = el)}
      position={[p.x, p.y, p.z]} rotation={p.rot} scale={p.s}>
      {/* the island */}
      <mesh geometry={geos[p.gi]}>
        <meshBasicMaterial color="#1B1030" fog={false} toneMapped={false} />
      </mesh>
      {/* the dimension inside it — a smaller, brighter core showing
          through the facets, and the reason these read as POCKETS */}
      <mesh geometry={geos[p.gi]} scale={0.74}>
        <meshBasicMaterial color={p.glow} transparent opacity={0.5}
          fog={false} toneMapped={false} />
      </mesh>
      {/* the leak — a soft shell, so the light doesn't stop at the rock */}
      <mesh geometry={geos[p.gi]} scale={1.5}>
        <meshBasicMaterial color={p.glow} transparent opacity={0.09}
          side={THREE.BackSide} depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
    </group>
  ));
}

// ════════════════════════════════════════════════════════════
//  4 · THE OCEAN — of Khao, or of Anima
// ════════════════════════════════════════════════════════════
//  The track hangs over it. Scrolls on two axes at two rates, so
//  it never reads as a repeating tile.
function VoidOcean({ bounds, tint, deep }) {
  const tex = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 256; c.height = 256;
    const g = c.getContext("2d");
    const R = rnd(hash("ocean" + tint));
    g.fillStyle = deep; g.fillRect(0, 0, 256, 256);
    // caustic filaments — liquid light, not water
    for (let i = 0; i < 60; i++) {
      g.strokeStyle = tint;
      g.globalAlpha = 0.05 + R() * 0.16;
      g.lineWidth = 0.6 + R() * 2.2;
      g.beginPath();
      let x = R() * 256, y = R() * 256;
      g.moveTo(x, y);
      for (let k = 0; k < 5; k++) {
        x += (R() - 0.5) * 90; y += (R() - 0.5) * 60;
        g.lineTo(x, y);
      }
      g.stroke();
    }
    g.globalAlpha = 1;
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(9, 9);
    return t;
  }, [tint, deep]);

  const mat = useRef();
  useFrame(({ clock }) => {
    if (!mat.current || !mat.current.map) return;
    const t = clock.elapsedTime;
    mat.current.map.offset.set(t * 0.006, t * 0.011);
    mat.current.opacity = 0.80 + Math.sin(t * 0.35) * 0.06;
  });

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]}
      position={[bounds.cx, bounds.minH - bounds.radius * 0.42, bounds.cz]}>
      <planeGeometry args={[bounds.radius * 7, bounds.radius * 7]} />
      <meshBasicMaterial ref={mat} map={tex} transparent opacity={0.82}
        fog={false} toneMapped={false} />
    </mesh>
  );
}

// ════════════════════════════════════════════════════════════
//  5 · RADIATING LYT / LYF
// ════════════════════════════════════════════════════════════
//  Columns standing off the ocean. Lyt is the light that harms;
//  Lyf is the skin that endures. They alternate, because the two
//  are always a pair in this Empire.
function Radiance({ bounds, seed = 5 }) {
  const items = useMemo(() => {
    const R = rnd(seed);
    const out = [];
    for (let i = 0; i < N_COLUMNS; i++) {
      const a = R() * Math.PI * 2;
      const d = bounds.radius * (1.7 + R() * 0.7);
      out.push({
        key: i,
        x: bounds.cx + Math.cos(a) * d,
        z: bounds.cz + Math.sin(a) * d,
        w: bounds.radius * (0.05 + R() * 0.09),
        h: bounds.radius * (0.9 + R() * 1.5),
        phase: R() * 7,
        hue: i % 2 === 0 ? IE_LIGHT.lyt : IE_LIGHT.lyf,
      });
    }
    return out;
  }, [bounds, seed]);

  const refs = useRef([]);
  const { camera } = useThree();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    for (let i = 0; i < refs.current.length; i++) {
      const m = refs.current[i], p = items[i];
      if (!m || !p) continue;
      // billboard on Y ONLY — a column of radiance that tips toward
      // the camera stops being a column
      m.rotation.y = Math.atan2(camera.position.x - p.x, camera.position.z - p.z);
      // radiance SWELLS. It does not flicker — flicker is a fault,
      // and these are not faulty, they are enormous.
      m.material.opacity = 0.10 + (0.5 + Math.sin(t * 0.33 + p.phase) * 0.5) * 0.16;
      m.scale.x = 1 + Math.sin(t * 0.21 + p.phase) * 0.10;
    }
  });

  const baseY = bounds.minH - bounds.radius * 0.42;
  return items.map((p, i) => (
    <mesh key={p.key} ref={el => (refs.current[i] = el)}
      position={[p.x, baseY + p.h / 2, p.z]}>
      <planeGeometry args={[p.w, p.h]} />
      <meshBasicMaterial color={p.hue} transparent opacity={0.18}
        side={THREE.DoubleSide} depthWrite={false} fog={false} toneMapped={false} />
    </mesh>
  ));
}

// ════════════════════════════════════════════════════════════
//  6 · THE RISE
// ════════════════════════════════════════════════════════════
//  Motes going UP, off the ocean, past the track. Direction is
//  characterisation: everything in this Empire rises, which is
//  precisely why the Slag Golem's dripping was legible. Nothing
//  here falls.
function TheRise({ bounds, seed = 3 }) {
  const items = useMemo(() => {
    const R = rnd(seed);
    const out = [];
    for (let i = 0; i < N_MOTES; i++) {
      const a = R() * Math.PI * 2;
      const d = bounds.radius * (0.9 + R() * 1.5);
      out.push({
        key: i,
        x: bounds.cx + Math.cos(a) * d,
        z: bounds.cz + Math.sin(a) * d,
        s: bounds.radius * (0.006 + R() * 0.016),
        rate: 0.02 + R() * 0.05,
        off: R(),
        hue: R() < 0.5 ? IE_LIGHT.khao : IE_LIGHT.anima,
      });
    }
    return out;
  }, [bounds, seed]);

  const refs = useRef([]);
  const baseY = bounds.minH - bounds.radius * 0.42;
  const span = bounds.radius * 1.9;
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    for (let i = 0; i < refs.current.length; i++) {
      const m = refs.current[i], p = items[i];
      if (!m || !p) continue;
      const cyc = (t * p.rate + p.off) % 1;
      m.position.y = baseY + cyc * span;
      m.material.opacity = 0.55 * Math.sin(cyc * Math.PI);  // fades in and out, never pops
    }
  });

  return items.map((p, i) => (
    <mesh key={p.key} ref={el => (refs.current[i] = el)} position={[p.x, baseY, p.z]}>
      <sphereGeometry args={[p.s, 6, 5]} />
      <meshBasicMaterial color={p.hue} transparent opacity={0.5}
        depthWrite={false} fog={false} toneMapped={false} />
    </mesh>
  ));
}

// ════════════════════════════════════════════════════════════
//  THE COMPOSITION
// ════════════════════════════════════════════════════════════
//  One component, sized entirely off the caller's bounds. Drop it
//  into any IE scene that has a `{cx, cz, radius, minH, maxH}` and
//  it fills the horizon.
//
//  `sky` lets a title recolour the world without editing this file
//  — Ruin Rush feeds it each track's mood, so the First Loop and
//  the Long Ruin hang under different heavens.
// ════════════════════════════════════════════════════════════
export default function UnstableWorld({ bounds, sky = {}, seed = 1 }) {
  const s = {
    eclipseRim: sky.eclipseRim || IE_LIGHT.omnis,
    eclipseCrack: sky.eclipseCrack || IE_LIGHT.anima,
    holeHot: sky.holeHot || IE_LIGHT.ruin,
    holeCool: sky.holeCool || IE_LIGHT.nullus,
    oceanTint: sky.oceanTint || IE_LIGHT.khao,
    oceanDeep: sky.oceanDeep || "#0B1430",
    shardA: sky.shardA || IE_LIGHT.nullus,
    shardB: sky.shardB || IE_LIGHT.omnis,
  };
  const b = bounds;
  const R = b.radius;

  // Placed on opposite arcs so the eye always has one of them, and
  // never both centred at once. Heights differ: the eclipse hangs
  // high and the hole sits low and heavy, which is the whole reason
  // they read as different KINDS of thing rather than two discs.
  // ⚑ KEPT INSIDE THE SKY DOME. The dome is radius 3.4 × track
  // radius; at the first placement the eclipse sat 2.97R out on the
  // horizontal alone and would have hung THROUGH the horizon. Any
  // element added here must stay under that figure — measure it,
  // don't eyeball it.
  const eclipsePos = [b.cx - R * 1.50, b.maxH + R * 0.95, b.cz + R * 1.60];  // 2.19R out
  const holePos    = [b.cx + R * 1.80, b.maxH + R * 0.30, b.cz - R * 1.50];  // 2.34R out

  return (
    <group>
      <VoidOcean bounds={b} tint={s.oceanTint} deep={s.oceanDeep} />
      <Radiance bounds={b} seed={seed * 7 + 5} />
      <CrackedEclipse pos={eclipsePos} size={R * 1.7} rim={s.eclipseRim} crack={s.eclipseCrack} />
      <RuinHole pos={holePos} size={R * 1.25} hot={s.holeHot} cool={s.holeCool} />
      <ShatteredDimensions bounds={b} glowA={s.shardA} glowB={s.shardB} seed={seed * 13 + 11} />
      <TheRise bounds={b} seed={seed * 3 + 2} />
    </group>
  );
}

function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h) + 1;
}
